import React, { type ReactElement } from 'react'
import { Box } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { Button, Icon, IconButton, TextField } from '~components/internal'
import { typography } from '~tokens/internal'
import { type ContactEmail0 } from '~types'

import './EmailInputTable.scss'

interface EmailInputRow {
  id: string
  email: string
  isValid: boolean
}

type EmailInputTableProps = {
  initialEmails?: ContactEmail0[]
  setEmails: React.Dispatch<React.SetStateAction<ContactEmail0[]>>
  onValidationChange?: (isValid: boolean) => void
}

const EmailInputTable = ({
  initialEmails,
  setEmails,
  onValidationChange,
}: EmailInputTableProps): ReactElement => {
  const [inputRows, setInputRows] = React.useState<EmailInputRow[]>(
    initialEmails && initialEmails.length > 0
      ? initialEmails.map((e) => ({ id: e.id, email: e.address, isValid: true }))
      : [{ id: uuidv4(), email: '', isValid: false }],
  )

  const _areAllEmailsValid = React.useMemo(() => {
    const isValid = inputRows.every((row) => row.isValid || row.email === '')

    onValidationChange?.(isValid)
    return isValid
  }, [inputRows, onValidationChange])

  const addEmailInput = (): void => {
    setInputRows((prev) => [...prev, { id: uuidv4(), email: '', isValid: false }])
  }

  const removeEmailInput = (id: string): void => {
    setInputRows((prev) => {
      const newRows = prev.filter((row) => row.id !== id)
      const validEmails = newRows
        .filter((row) => row.isValid)
        .map((row) => ({
          id: row.id,
          email: row.id,
          contact: row.id,
          address: row.email,
          primary: false,
          verified: false,
          ses_verified: false,
          signature: '',
        }))
      setEmails(validEmails)
      return newRows
    })
  }

  const validateEmail = (email: string): boolean => {
    if (email === '') return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleEmailChange = (id: string, newEmail: string): void => {
    const isValid = validateEmail(newEmail)
    setInputRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, email: newEmail, isValid } : row)),
    )

    const validEmails = inputRows
      .map((row) => ({
        id: row.id === id ? id : row.id,
        email: row.id === id ? newEmail : row.email,
        isValid: row.id === id ? isValid : row.isValid,
      }))
      .filter((row) => row.isValid)
      .map((row) => ({
        id: row.id,
        email: row.id,
        contact: row.id,
        address: row.email,
        primary: false,
        verified: false,
        ses_verified: false,
        signature: '',
      }))

    setEmails(validEmails)
  }

  return (
    <Box className='email-input-table'>
      <Box className='email-input-table__list'>
        {inputRows.map((row, index) => (
          <Box key={row.id} className='email-input-table__row'>
            <Box className='email-input-table__field'>
              <TextField
                label={index === 0 ? 'Email' : undefined}
                value={row.email}
                onChange={(e) => handleEmailChange(row.id, e.target.value)}
                error={row.email !== '' && !row.isValid}
                errorText='Invalid email address'
                fullWidth
              />
            </Box>
            {inputRows.length > 1 && (
              <IconButton
                size='small'
                onClick={() => removeEmailInput(row.id)}
                className={index === 0 ? 'email-input-table__remove-button-first' : ''}
              >
                <Icon name='Remove' size={typography.fontSize.lg} />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          variant='text'
          size='small'
          width='fit'
          startIcon={<Icon name='AddCircleOutlined' />}
          onClick={addEmailInput}
        >
          Add another
        </Button>
      </Box>
    </Box>
  )
}

export default EmailInputTable
